<template>
  <b-card
    no-body
    class="team-today-attendance-panel team-mid-panel h-100 mb-0"
    :class="{ 'team-mid-panel--mobile-collapsed': mobileCollapsed }"
  >
    <b-card-header
      class="team-mid-panel__header d-flex align-items-center justify-content-between py-50 px-1 border-bottom"
      :class="{ 'team-mid-panel__header--toggle': isNarrowViewport }"
      :role="isNarrowViewport ? 'button' : null"
      :tabindex="isNarrowViewport ? 0 : null"
      :aria-expanded="isNarrowViewport ? String(!mobileCollapsed) : null"
      @click="toggleMobileCollapsed"
      @keydown.enter.prevent="toggleMobileCollapsed"
      @keydown.space.prevent="toggleMobileCollapsed"
    >
      <h6 class="mb-0 d-flex align-items-center text-body team-mid-panel__title">
        <feather-icon icon="ClockIcon" size="14" class="mr-50 text-primary" />
        Today's attendance
        <b-badge
          v-if="filterActive"
          variant="light-primary"
          pill
          class="ml-50 team-today-filter-badge"
        >
          Filtered
        </b-badge>
      </h6>
      <div class="d-flex align-items-center flex-shrink-0 team-mid-panel__header-actions">
        <div
          v-if="!isNonWorkingDay && totalCount > 0"
          class="team-today-search-wrap"
          @click.stop
          @keydown.stop
        >
          <feather-icon icon="SearchIcon" size="12" class="team-today-search-icon" />
          <b-form-input
            v-model="search"
            size="sm"
            placeholder="Search employee..."
            class="team-today-search"
          />
        </div>
        <feather-icon
          v-if="isNarrowViewport"
          :icon="mobileCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
          size="16"
          class="text-muted team-mid-panel__collapse-chevron flex-shrink-0"
        />
      </div>
    </b-card-header>

    <b-card-body
      v-show="!isNarrowViewport || !mobileCollapsed"
      class="team-mid-panel__body p-1 d-flex flex-column"
      :aria-hidden="isNarrowViewport && mobileCollapsed ? 'true' : null"
    >
      <div
        v-if="isNonWorkingDay"
        class="d-flex align-items-center justify-content-center text-muted small flex-grow-1 py-1"
      >
        <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
        <span>{{ nonWorkingLabel }}</span>
      </div>

      <div
        v-else-if="totalCount === 0"
        class="d-flex align-items-center justify-content-center text-muted small flex-grow-1 py-1"
      >
        <feather-icon icon="UsersIcon" size="14" class="mr-50" />
        <span>No team members in scope</span>
      </div>

      <template v-else>
        <div class="team-today-tabs mb-1 flex-shrink-0" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="team-today-tab"
            :class="{ 'team-today-tab--active': activeTab === tab.key }"
            :aria-selected="activeTab === tab.key ? 'true' : 'false'"
            role="tab"
            @click="activeTab = tab.key"
          >
            <span class="team-today-tab__label">{{ tab.label }}</span>
            <span
              class="team-today-tab__count"
              :class="activeTab === tab.key ? 'team-today-tab__count--active' : 'team-today-tab__count--idle'"
            >
              {{ tab.count }}
            </span>
          </button>
        </div>

        <div class="team-mid-table team-today-table-wrap">
          <div class="team-mid-table__scroll team-mid-list">
            <div class="team-mid-table__header team-mid-table__grid--today team-today-header-row px-50">
              <span class="team-today-col-name team-mid-table__th team-today-th">Name</span>
              <span class="team-today-col-in team-mid-table__th team-today-th">In</span>
              <span class="team-today-col-out team-mid-table__th team-today-th">Out</span>
              <span class="team-today-col-hours team-mid-table__th team-today-th">Hours</span>
            </div>

            <div
              v-if="filteredRows.length === 0"
              class="d-flex align-items-center justify-content-center text-muted small py-2"
            >
              {{ search.trim() ? 'No matches' : 'No one in this list' }}
            </div>

            <b-list-group
              v-else
              flush
              class="team-mid-table__list team-today-list"
            >
              <b-list-group-item
                v-for="row in pagedRows"
                :key="row.empId || row.name"
                class="px-50 team-mid-table__row team-mid-table__grid--today team-today-row"
              >
                <span class="team-today-col-name d-flex align-items-center flex-wrap min-w-0">
                  <span
                    class="small font-weight-bolder text-truncate team-mid-table__name team-today-name"
                  >
                    {{ row.name }}
                  </span>
                  <span class="team-today-flags">
                    <b-badge
                      v-if="isLateIn(row)"
                      class="team-today-flag team-today-flag--late"
                    >Late IN</b-badge>
                    <b-badge
                      v-if="isEarlyOut(row)"
                      class="team-today-flag team-today-flag--early"
                    >Early Out</b-badge>
                  </span>
                </span>
                <span
                  class="team-today-col-in team-today-time"
                  :class="{
                    'team-today-time--empty': !hasPunch(row.punchIn),
                    'team-today-time--late': isLateIn(row),
                  }"
                >
                  {{ displayPunch(row.punchIn) }}
                </span>
                <span
                  class="team-today-col-out team-today-time"
                  :class="{
                    'team-today-time--empty': !hasPunch(row.punchOut),
                    'team-today-time--early': isEarlyOut(row),
                  }"
                >
                  {{ displayPunch(row.punchOut) }}
                </span>
                <span
                  class="team-today-col-hours team-today-hours"
                  :class="{ 'team-today-hours--empty': !formatWorkHours(row) }"
                >
                  {{ formatWorkHours(row) || '—' }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </div>

          <div
            v-if="filteredRows.length > 0"
            class="team-mid-table__pager d-flex align-items-center justify-content-between flex-wrap px-50 py-25"
          >
            <span class="team-mid-table__pager-meta text-muted">
              Showing {{ pageRangeStart }}–{{ pageRangeEnd }} of {{ filteredRows.length }}
            </span>
            <b-pagination
              v-if="filteredRows.length > pageSize"
              v-model="currentPage"
              :total-rows="filteredRows.length"
              :per-page="pageSize"
              size="sm"
              pills
              align="right"
              class="mb-0 team-mid-table__pagination"
              :hide-goto-end-buttons="true"
              limit="5"
            />
          </div>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BFormInput,
  BListGroup,
  BListGroupItem,
  BPagination,
} from 'bootstrap-vue'
import {
  parseTimeToMins,
  formatMinsAsTime,
  getLateInThresholdMins,
  getEarlyOutThresholdMins,
  isLateInPunch,
  isEarlyOutPunch,
  SHIFT_GRACE_MINS,
} from '@/utils/attendance-shift-thresholds'

const PAGE_SIZE = 10

export default {
  name: 'TeamTodayAttendancePanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BFormInput,
    BListGroup,
    BListGroupItem,
    BPagination,
  },
  props: {
    present: {
      type: Array,
      default: () => [],
    },
    absent: {
      type: Array,
      default: () => [],
    },
    /** Approved WFH today (personal or company-wide) — dedicated tab */
    wfh: {
      type: Array,
      default: () => [],
    },
    isNonWorkingDay: {
      type: Boolean,
      default: false,
    },
    nonWorkingLabel: {
      type: String,
      default: 'Non-working day',
    },
    /** True when Team snapshot punch filters are applied to this roster */
    filterActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const narrow = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 991.98px)').matches
      : false
    return {
      activeTab: 'present',
      search: '',
      currentPage: 1,
      pageSize: PAGE_SIZE,
      /** Phone/tablet (≤991): stays collapsed until user toggles — match Org Snapshot */
      mobileCollapsed: true,
      isNarrowViewport: narrow,
    }
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this._panelMq = window.matchMedia('(max-width: 991.98px)')
    this._onPanelMq = e => {
      const nowNarrow = e.matches
      if (nowNarrow && !this.isNarrowViewport) {
        this.mobileCollapsed = true
      }
      this.isNarrowViewport = nowNarrow
    }
    this.isNarrowViewport = this._panelMq.matches
    if (this.isNarrowViewport) {
      this.mobileCollapsed = true
    }
    if (this._panelMq.addEventListener) {
      this._panelMq.addEventListener('change', this._onPanelMq)
    } else if (this._panelMq.addListener) {
      this._panelMq.addListener(this._onPanelMq)
    }
  },
  beforeDestroy() {
    if (!this._panelMq || !this._onPanelMq) return
    if (this._panelMq.removeEventListener) {
      this._panelMq.removeEventListener('change', this._onPanelMq)
    } else if (this._panelMq.removeListener) {
      this._panelMq.removeListener(this._onPanelMq)
    }
  },
  computed: {
    presentCount() {
      return (this.present || []).length
    },
    absentCount() {
      return (this.absent || []).length
    },
    wfhCount() {
      return (this.wfh || []).length
    },
    totalCount() {
      return this.presentCount + this.absentCount + this.wfhCount
    },
    tabs() {
      return [
        { key: 'present', label: 'In', count: this.presentCount },
        { key: 'absent', label: 'Not in', count: this.absentCount },
        { key: 'wfh', label: 'WFH', count: this.wfhCount },
      ]
    },
    activeRows() {
      if (this.activeTab === 'absent') return this.absent || []
      if (this.activeTab === 'wfh') return this.wfh || []
      return this.present || []
    },
    /** Name filter within the active tab; tab counts stay unfiltered */
    filteredRows() {
      const rows = this.activeRows || []
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter((row) => {
        const name = (row && row.name != null ? String(row.name) : '').toLowerCase()
        return name.includes(q)
      })
    },
    pageRangeStart() {
      if (this.filteredRows.length === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageRangeEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredRows.length)
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    /** Stable key for punch/employee filter + roster shape (avoids reset on computed array rebuilds) */
    rosterFilterKey() {
      const firstId = (rows) => {
        const row = (rows || [])[0]
        return row ? String(row.empId || row.name || '') : ''
      }
      return [
        this.filterActive ? '1' : '0',
        this.presentCount,
        this.absentCount,
        this.wfhCount,
        firstId(this.present),
        firstId(this.absent),
        firstId(this.wfh),
      ].join('|')
    },
  },
  watch: {
    totalCount: {
      immediate: true,
      handler() {
        this.ensureActiveTab()
      },
    },
    presentCount() {
      this.ensureActiveTab()
    },
    absentCount() {
      this.ensureActiveTab()
    },
    wfhCount() {
      this.ensureActiveTab()
    },
    activeTab() {
      this.resetPage()
    },
    search() {
      this.resetPage()
    },
    rosterFilterKey() {
      this.resetPage()
    },
    filteredRows: {
      handler(rows) {
        const totalPages = Math.max(1, Math.ceil((rows || []).length / this.pageSize))
        if (this.currentPage > totalPages) this.currentPage = totalPages
      },
    },
  },
  methods: {
    toggleMobileCollapsed() {
      if (!this.isNarrowViewport) return
      this.mobileCollapsed = !this.mobileCollapsed
    },
    resetPage() {
      this.currentPage = 1
    },
    hasPunch(value) {
      if (value == null || value === '') return false
      const s = String(value).trim()
      if (!s || s === '-' || s === '--' || s === '—' || s === '–') return false
      return true
    },
    displayPunch(value) {
      if (!this.hasPunch(value)) return '—'
      return String(value).trim()
    },
    timeToMins(value) {
      if (!this.hasPunch(value)) return null
      return parseTimeToMins(value)
    },
    isLateIn(row) {
      if (!row) return false
      return isLateInPunch(row.punchIn, row)
    },
    isEarlyOut(row) {
      if (!row) return false
      return isEarlyOutPunch(row.punchOut, row)
    },
    lateInTitle(row) {
      const threshold = formatMinsAsTime(getLateInThresholdMins(row))
      return `Punch-in after ${threshold} (shift start + ${SHIFT_GRACE_MINS}m grace)`
    },
    earlyOutTitle(row) {
      const threshold = formatMinsAsTime(getEarlyOutThresholdMins(row))
      return `Punch-out before ${threshold} (shift end − ${SHIFT_GRACE_MINS}m grace)`
    },
    formatWorkHours(row) {
      if (!row) return null
      const inMins = this.timeToMins(row.punchIn)
      const outMins = this.timeToMins(row.punchOut)
      if (inMins == null || outMins == null) return null
      let diff = outMins - inMins
      if (diff < 0) diff += 24 * 60
      if (diff <= 0) return null
      const hours = Math.floor(diff / 60)
      const minutes = diff % 60
      if (minutes === 0) return `${hours}h`
      if (minutes === 30) return `${hours}.5h`
      return `${hours}h ${minutes}m`
    },
    /** Used by Team snapshot chips to jump into In / Not in / WFH */
    setActiveTab(tabKey) {
      const allowed = ['present', 'absent', 'wfh']
      if (!allowed.includes(tabKey)) return
      this.activeTab = tabKey
    },
    ensureActiveTab() {
      const order = ['present', 'absent', 'wfh']
      const counts = {
        present: this.presentCount,
        absent: this.absentCount,
        wfh: this.wfhCount,
      }
      if (counts[this.activeTab] > 0) return
      const next = order.find((key) => counts[key] > 0)
      if (next) this.activeTab = next
    },
  },
}
</script>

<style src="./TeamMidTable.css"></style>

<style lang="scss" scoped>
.team-today-attendance-panel {
  border: 1px solid #e8ecf0;
  box-shadow: none;
  border-radius: 10px;
  overflow: hidden;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.team-mid-panel__header-actions {
  gap: 0.4rem;
  margin-left: auto;
}

.team-today-attendance-panel :deep(.card-header) {
  background: #fff;
  flex-shrink: 0;
}

.team-today-attendance-panel :deep(.card-header) h6 {
  font-size: 0.8rem;
  font-weight: 600;
}

.team-today-filter-badge {
  font-size: 0.55rem;
  font-weight: 700;
}

.team-today-search-wrap {
  position: relative;
  flex: 0 1 9.5rem;
  min-width: 7rem;
  max-width: 11rem;
}

.team-today-search-icon {
  position: absolute;
  left: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
}

.team-today-search {
  font-size: 0.7rem;
  padding-left: 1.55rem !important;
  min-height: 1.7rem;
  border-color: #e5e7eb;
}

.team-today-search:focus {
  border-color: #99f6e4;
  box-shadow: 0 0 0 0.15rem rgba(13, 148, 136, 0.12);
}

.team-today-attendance-panel :deep(.card-body) {
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.team-today-tabs {
  display: flex;
  gap: 0.25rem;
  width: 100%;
}

.team-today-tab {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin: 0;
  padding: 0.26rem 0.3rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #4b5563;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.team-today-tab:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #374151;
}

.team-today-tab--active {
  color: #0f766e;
  border-color: #0d9488;
  background: #f0fdfa;
  box-shadow: none;
}

.team-today-tab--active:hover {
  color: #0f766e;
  border-color: #0d9488;
  background: #ecfdf5;
}

.team-today-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  height: 1.1rem;
  padding: 0 0.3rem;
  border-radius: 999px;
  font-size: 0.55rem;
  font-weight: 700;
  line-height: 1;
}

.team-today-tab__count--idle {
  background: #e5e7eb;
  color: #374151;
}

.team-today-tab__count--active {
  background: #0d9488;
  color: #ffffff;
}

/* Table chrome/header/rows/name/scroll: shared via TeamMidTable.css (.team-mid-table) */

.team-today-col-name {
  min-width: 0;
  font-size: 0.72rem;
  gap: 0.2rem 0.28rem;
}

.min-w-0 {
  min-width: 0;
}

.team-today-flags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.15rem;
  flex-shrink: 0;
}

.team-today-flag {
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.1rem 0.28rem;
  border-radius: 4px;
  line-height: 1.15;
  border: 1px solid transparent;
}

.team-today-flag--late {
  background: #fff7ed;
  color: #c2410c;
  border-color: #fed7aa;
}

.team-today-flag--early {
  background: #ecfeff;
  color: #0e7490;
  border-color: #a5f3fc;
}

.team-today-col-in,
.team-today-col-out,
.team-today-col-hours {
  white-space: nowrap;
  text-align: left;
  justify-self: start;
}

.team-today-time {
  display: inline-block;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  font-weight: 600;
  color: #374151;
  font-size: 0.7rem;
  letter-spacing: 0.01em;
}

.team-today-time--empty {
  color: #c4c9d2;
  font-weight: 500;
}

.team-today-time--late {
  color: #c2410c;
}

.team-today-time--early {
  color: #0e7490;
}

.team-today-hours {
  display: inline-block;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #0f766e;
  font-size: 0.68rem;
}

.team-today-hours--empty {
  color: #c4c9d2;
  font-weight: 500;
}

@media (max-width: 991px) {
  .team-mid-panel__header--toggle {
    cursor: pointer;
    user-select: none;
    min-height: 2.5rem;
    gap: 0.5rem;
  }

  .team-mid-panel__header--toggle:hover {
    background: #f8fafb;
  }

  .team-mid-panel__header--toggle:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(0, 172, 153, 0.25);
  }

  .team-mid-panel__collapse-chevron {
    margin-left: 0.15rem;
  }

  .team-mid-panel--mobile-collapsed {
    height: auto !important;
    min-height: 0 !important;
  }

  .team-today-attendance-panel.team-mid-panel--mobile-collapsed > .card-body,
  .team-today-attendance-panel.team-mid-panel--mobile-collapsed .team-mid-panel__body {
    display: none !important;
  }
}
</style>
